import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTelegramPlane, faXTwitter, faGithub } from '@fortawesome/free-brands-svg-icons';
import { faHeart } from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from '../../ui';
import { cn } from '../../ui/utils/cn';
import { SERVER_URL } from '../../config/env';
import { useAppSelector } from '../../store/hooks';
import { apiClient } from '../../api/client';

type HealthStatus = 'loading' | 'healthy' | 'partial' | 'error';

interface PublicStatsService {
  name: string;
  url: string;
  status: string;
  version: string | null;
  pingMs: number | null;
  error: string | null;
}

interface PublicStatsResponse {
  aotd: { count: number; latest: unknown };
  coffee: { ratesCount: number; shopsCount: number };
  users: { total: number };
  health: { services: PublicStatsService[] };
}

interface IrlDevice {
  deviceId: string;
  shortId?: string;
  status: string;
}

const publicStatsUrl = SERVER_URL ? `${SERVER_URL}/api/public` : '/api/public';

const StatusFooter: React.FC = () => {
  const [fetchStatus, setFetchStatus] = useState<HealthStatus>('loading');
  const [services, setServices] = useState<PublicStatsService[]>([]);
  const [isLiked, setIsLiked] = useState<boolean>(false);
  const [claimedDevices, setClaimedDevices] = useState<IrlDevice[]>([]);

  const auth = useAppSelector((s) => s.auth);
  const authReadyForIrl = auth.bootstrapStatus === 'ready' && Boolean(auth.token);

  useEffect(() => {
    if (!authReadyForIrl) {
      setClaimedDevices([]);
      return;
    }
    const ctrl = new AbortController();
    (async () => {
      try {
        const res = await apiClient.get<{ devices?: IrlDevice[] }>('/api/aotd-irl/devices', {
          signal: ctrl.signal,
        });
        if (ctrl.signal.aborted) return;
        const devices = res.data.devices ?? [];
        setClaimedDevices(devices.filter((d) => d.status === 'registered'));
      } catch {
        // non-essential UI
      }
    })();
    return () => ctrl.abort();
  }, [authReadyForIrl]);

  useEffect(() => {
    const fetchPublicStats = async (): Promise<void> => {
      try {
        const response = await fetch(publicStatsUrl);
        const data: PublicStatsResponse | null = await response.json().catch(() => null);
        if (!response.ok || !data?.health?.services) {
          setFetchStatus('error');
          setServices([]);
          return;
        }
        setServices(data.health.services);
        const statuses = data.health.services.map((s) => (s.status === 'ok' ? 'healthy' : 'error'));
        if (statuses.every((s) => s === 'healthy')) {
          setFetchStatus('healthy');
        } else if (statuses.every((s) => s === 'error')) {
          setFetchStatus('error');
        } else {
          setFetchStatus('partial');
        }
      } catch {
        setFetchStatus('error');
        setServices([]);
      }
    };

    fetchPublicStats();
    const interval = setInterval(fetchPublicStats, 90000); // poll every 90s
    return () => clearInterval(interval);
  }, []);

  const getStatusColorClass = (status: HealthStatus): string => {
    switch (status) {
      case 'healthy':
        return 'bg-status-completed';
      case 'partial':
        return 'bg-status-generating';
      case 'error':
        return 'bg-status-failed';
      case 'loading':
      default:
        return 'bg-status-loading';
    }
  };

  const getServiceStatus = (svc: PublicStatsService): HealthStatus =>
    svc.status === 'ok' ? 'healthy' : 'error';

  const getOverallStatus = (): HealthStatus => fetchStatus;

  const claimedCount = claimedDevices.length;
  const firstRegistered = claimedDevices[0];
  const singleFrameLabel =
    firstRegistered &&
    (firstRegistered.shortId
      ? firstRegistered.shortId.toUpperCase()
      : firstRegistered.deviceId.slice(-4).toUpperCase());

  return (
    <>
      {/* top separator line so it reads as detached from page content */}
      <div
        className="fixed bottom-[26px] left-0 w-full h-px bg-ink/20 hidden md:block z-50"
        aria-hidden
      />
      <div
        className={cn(
          'fixed bottom-0 left-0 w-full h-[26px]',
          'hidden md:flex items-center justify-between px-4',
          // extruded bar styling
          'text-xs z-50 bg-paper text-foreground border-brutal border-ink shadow-brutal-sm'
        )}
        style={{ borderTopWidth: 'var(--ui-border-width)' }}
      >

        <div className="flex items-center gap-4">
          <Link
            to="/about"
            className="text-foreground/60 hover:text-foreground transition-colors no-underline"
          >
            about
          </Link>
          <Link
            to="/contact"
            className="text-foreground/60 hover:text-foreground transition-colors no-underline"
          >
            contact
          </Link>
          <Link
            to="/privacy"
            className="text-foreground/60 hover:text-foreground transition-colors no-underline"
          >
            privacy
          </Link>
          <Link
            to="/cookies"
            className="text-foreground/60 hover:text-foreground transition-colors no-underline"
          >
            cookies
          </Link>
          <Link
            to="/terms"
            className="text-foreground/60 hover:text-foreground transition-colors no-underline"
          >
            terms
          </Link>
          <Link
            to="/contribute"
            className="text-foreground/80 hover:text-foreground transition-colors no-underline font-semibold"
          >
            contribute
          </Link>

          <div className="w-px h-3 bg-ink/20 mx-2" />

          <a
            href="https://x.com/n450s"
            target="_blank"
            rel="noopener noreferrer"
            className="text-foreground/60 hover:text-foreground transition-colors"
          >
            <FontAwesomeIcon icon={faXTwitter} className="text-sm" />
          </a>
          <a
            href="https://github.com/n45os"
            target="_blank"
            rel="noopener noreferrer"
            className="text-foreground/60 hover:text-foreground transition-colors"
          >
            <FontAwesomeIcon icon={faGithub} className="text-sm" />
          </a>
          <a
            href="https://t.me/+CxzdB8kP_3RlZGQ0"
            target="_blank"
            rel="noopener noreferrer"
            className="text-foreground/60 hover:text-foreground transition-colors"
          >
            <FontAwesomeIcon icon={faTelegramPlane} className="text-sm" />
          </a>

          {claimedCount > 0 && (
            <>
              <div className="w-px h-3 bg-ink/20 mx-2" />
              <Tooltip
                content={`your AOTD-IRL frame${claimedCount > 1 ? 's' : ''} — manage them`}
                side="top"
              >
                <Link
                  to="/aotd-irl/my"
                  className="text-foreground/60 hover:text-foreground transition-colors no-underline"
                >
                  <span className="mr-1" aria-hidden>
                    🖼
                  </span>
                  {claimedCount === 1
                    ? `${singleFrameLabel} frame`
                    : `${claimedCount} frames`}
                </Link>
              </Tooltip>
            </>
          )}
        </div>

        <div className="flex items-center gap-4">
          <Tooltip
            content={
              <div className="flex flex-col gap-1 text-xs">
                {services.length === 0 && fetchStatus === 'loading' && (
                  <span className="text-gray-500">checking...</span>
                )}
                {services.length === 0 && fetchStatus === 'error' && (
                  <span className="text-gray-500">could not load status</span>
                )}
                {services
                  .filter((svc) => svc.name !== "P14nner API" && svc.name !== "P14nner Frontend")
                  .map((svc) => (
                  <span key={svc.name} className="flex items-center gap-1.5">
                    {svc.name}{' '}
                    <span
                      className={cn('h-1.5 w-1.5 rounded-full inline-block', getStatusColorClass(getServiceStatus(svc)))}
                    />
                    {svc.version != null && (
                      <span className="text-gray-500 ml-1">v{svc.version}</span>
                    )}
                    {svc.pingMs != null && (
                      <span className="text-gray-400 ml-1">{svc.pingMs}ms</span>
                    )}
                  </span>
                  ))}
              </div>
            }
            side="top"
          >
            <span className="flex items-center gap-1 cursor-pointer text-muted/90 hover:text-foreground transition-colors">
              status{' '}
              <span
                className={cn('h-2 w-2 rounded-full inline-block', getStatusColorClass(getOverallStatus()))}
              />
            </span>
          </Tooltip>

          <Tooltip
            content="site owner/creator: owner of n450s.com domain. username: n450s. all rights reserved."
            side="top"
          >
            <span className="underline decoration-dotted cursor-help text-muted/90 hover:text-foreground transition-colors">
              rights
            </span>
          </Tooltip>

          <button
            onClick={() => setIsLiked(!isLiked)}
            className={cn(
              'flex items-center justify-center transition-colors',
              isLiked ? 'text-pink hover:text-pink-hover' : 'text-muted/90 hover:text-pink'
            )}
            aria-label="like"
          >
            <FontAwesomeIcon icon={faHeart} className={cn('text-xs', isLiked && 'fill-current')} />
          </button>
        </div>
      </div>
    </>
  );
};

export default StatusFooter;

